import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Sparkles } from 'lucide-react';

export function SplashScreen() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Inicializando sistema');

  useEffect(() => {
    const loadingSteps = [
      'Inicializando sistema',
      'Conectando servicios',
      'Cargando módulos',
      'Preparando automatizaciones',
      'Configurando agentes IA',
      'Sincronizando datos',
      'Finalizando configuración',
      'Listo'
    ];

    let currentProgress = 0;
    let stepIndex = 0;

    // 100ms * 100 = 10000ms = 10 segundos de carga
    const progressInterval = setInterval(() => {
      currentProgress += 1;
      setProgress(currentProgress);

      // Cambiar texto cada ~12.5% de progreso
      const newStepIndex = Math.floor(currentProgress / 12.5);
      if (newStepIndex !== stepIndex && newStepIndex < loadingSteps.length) {
        stepIndex = newStepIndex;
        setLoadingText(loadingSteps[stepIndex]);
      }

      if (currentProgress >= 100) {
        clearInterval(progressInterval);
        setLoadingText('Listo');
        setTimeout(() => {
          navigate('/login');
        }, 800);
      }
    }, 100); // 100ms * 100 = 10 segundos

    return () => {
      clearInterval(progressInterval);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4"
         style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30"
           style={{
             backgroundImage: 'linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)',
             backgroundSize: '24px 24px'
           }} />

      {/* Main content */}
      <div className="relative z-10 text-center space-y-8 px-4 max-w-2xl w-full">
        {/* Logo animation */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-6">
            {/* Glow effect */}
            <div className="absolute inset-0 rounded-full animate-pulse"
                 style={{
                   background: 'rgba(59, 130, 246, 0.3)',
                   filter: 'blur(20px)',
                   transform: 'scale(1.5)'
                 }} />
            {/* Logo container */}
            <div className="relative flex items-center justify-center w-24 h-24">
              <img src="/logo-zaphub.png" alt="ZAPHUB" className="w-24 h-24 object-contain" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl font-bold tracking-tight mb-3"
              style={{ color: '#1e293b' }}>
            ZAPHUB
          </h1>
          <p className="text-lg font-medium" style={{ color: '#3b82f6' }}>
            2.0
          </p>

          <div className="flex items-center justify-center gap-2 mt-4" style={{ color: '#64748b' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#3b82f6' }} />
            <p className="text-sm">
              Plataforma de Automatización Empresarial
            </p>
            <Sparkles className="w-4 h-4" style={{ color: '#3b82f6' }} />
          </div>
        </div>

        {/* Progress section */}
        <div className="space-y-4">
          {/* Progress bar container */}
          <div className="relative">
            <div className="relative rounded-xl shadow-lg p-6"
                 style={{
                   background: 'white',
                   border: '1px solid #e2e8f0'
                 }}>
              <div className="space-y-4">
                {/* Progress bar background */}
                <div className="relative h-4 rounded-full overflow-hidden"
                     style={{ background: '#e2e8f0' }}>
                  {/* Progress bar fill */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all duration-150 ease-out"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                      boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
                    }}
                  >
                    {/* Shimmer effect */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        animation: 'shimmer 1.5s infinite'
                      }}
                    />
                  </div>
                </div>

                {/* Progress text */}
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: '#64748b' }}>{loadingText}...</span>
                  <span className="font-mono font-bold text-lg" style={{ color: '#3b82f6' }}>
                    {progress}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Loading dots */}
          <div className="flex items-center justify-center gap-3 pt-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-3 h-3 rounded-full"
                style={{
                  background: '#3b82f6',
                  animation: `pulse 1.5s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`
                }}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-2">
          <p className="text-xs" style={{ color: '#94a3b8' }}>
            © 2024 ZAPHUB. Sistema de automatización empresarial.
          </p>
          <button
            onClick={() => navigate('/como-funciona')}
            className="text-xs underline underline-offset-2 transition-opacity hover:opacity-80"
            style={{ color: '#3b82f6' }}
          >
            ¿Cómo funciona ZAPHUB?
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
